import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Filter, CheckCircle, XCircle, Clock } from 'lucide-react';
import { aiRegistryClient, type AIRun } from '../../services/ai-registry-client';

export type { AIRun };

export interface AIRunLocal {
  id: string;
  flowId: string;
  modelId: string;
  input: {
    data: any;
    size: number;
  };
  output?: {
    data: any;
    size: number;
  };
  context: {
    userId?: string;
    isAdmin: boolean;
  };
  tokens?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  cost?: {
    amount: number;
    currency: string;
  };
  performance: {
    duration: number;
  };
  result: {
    status: 'success' | 'error' | 'timeout' | 'cancelled';
    error?: {
      message: string;
    };
  };
  startedAt: string;
  completedAt?: string;
}

export function RunsMain() {
  const [runs, setRuns] = useState<AIRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [flowFilter, setFlowFilter] = useState<string>('');

  const loadRuns = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (flowFilter) filters.flowId = flowFilter;

      const data = await aiRegistryClient.getRuns(filters);
      setRuns(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load runs');
      console.error('Error loading runs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, [statusFilter, flowFilter]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'timeout':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <XCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'timeout':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="w-8 h-8 text-uw-interactive-primary" />
          <h1 className="text-3xl font-bold text-uw-text-primary">AI Runs</h1>
        </div>
        <Link
          to="/ai/analytics"
          className="inline-flex items-center px-4 py-2 bg-uw-interactive-primary text-white rounded-md hover:bg-uw-interactive-primary-hover transition-colors"
        >
          <Filter className="w-4 h-4 mr-2" />
          View Analytics
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-4 mb-6">
        <div className="flex gap-4 items-end">
          <div className="w-48">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="timeout">Timeout</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-uw-text-secondary mb-1">
              Flow ID
            </label>
            <input
              type="text"
              value={flowFilter}
              onChange={(e) => setFlowFilter(e.target.value)}
              placeholder="Filter by flow ID..."
              className="w-full px-4 py-2 border border-uw-border-default rounded-md bg-uw-surface-default text-uw-text-primary focus:ring-2 focus:ring-uw-interactive-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Run List */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uw-interactive-primary"></div>
          <p className="mt-4 text-uw-text-secondary">Loading runs...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {!loading && !error && runs.length === 0 && (
        <div className="bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-12 text-center">
          <Activity className="w-16 h-16 text-uw-text-tertiary mx-auto mb-4" />
          <p className="text-uw-text-secondary">No runs found</p>
        </div>
      )}

      {!loading && !error && runs.length > 0 && (
        <div className="space-y-3">
          {runs.map((run) => (
            <Link
              key={run.id}
              to={`/ai/runs/${run.id}`}
              className="block bg-uw-surface-default rounded-lg shadow border border-uw-border-default p-4 hover:shadow-lg hover:border-uw-interactive-primary transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {getStatusIcon(run.result.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm text-uw-text-primary">
                        {run.id.substring(0, 8)}...
                      </span>
                      <span className="text-sm text-uw-text-secondary">{run.flowId}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(run.result.status)}`}>
                        {run.result.status}
                      </span>
                    </div>
                    {run.result.error && (
                      <p className="text-sm text-red-600 dark:text-red-400 mb-2">
                        {run.result.error.message}
                      </p>
                    )}
                    <div className="flex items-center gap-6 text-sm text-uw-text-tertiary">
                      <span>Duration: {formatDuration(run.performance.duration)}</span>
                      {run.tokens && (
                        <span>Tokens: {run.tokens.totalTokens.toLocaleString()}</span>
                      )}
                      {run.cost && (
                        <span>
                          Cost: ${run.cost.amount.toFixed(4)} {run.cost.currency}
                        </span>
                      )}
                      <span>Started: {formatTimestamp(run.startedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
